import { MAX_DEPTH, colOf, inBounds } from '../sim/constants';
import type { Terrain } from '../sim/types';

export const TILE_W = 64;
export const TILE_H = 32;
export const HALF_W = 32;
export const HALF_H = 16;
/** Screen pixels per excavation level. = TILE_H/2 so cliff faces stack in uniform 16px strips. */
export const ELEV_STEP = 16;

/** Screen position of the TOP vertex of tile (tx, ty) at excavation depth d. */
export function tileTopX(tx: number, ty: number): number {
  return (tx - ty) * HALF_W;
}

export function tileTopY(tx: number, ty: number, depth: number): number {
  return (tx + ty) * HALF_H + depth * ELEV_STEP;
}

/** Screen position of a grid VERTEX (integer corner coordinates). */
export function vertexX(vx: number, vy: number): number {
  return (vx - vy) * HALF_W;
}

export function vertexY(vx: number, vy: number): number {
  return (vx + vy) * HALF_H;
}

/** Painter's-order key; larger = closer to camera. */
export function rowKey(tx: number, ty: number): number {
  return tx + ty;
}

export interface PickedTile {
  x: number;
  y: number;
  z: number;
}

/**
 * Screen(world-space) point -> tile, resolving elevation ambiguity by scanning
 * from the surface downward and returning the highest tile whose floor is
 * under the cursor. With a locked level, only that plane is considered.
 */
export function pickTile(
  worldX: number,
  worldY: number,
  terrain: Terrain,
  lockedLevel: number | null,
): PickedTile | null {
  let fallback: PickedTile | null = null;
  const zFrom = lockedLevel ?? 0;
  const zTo = lockedLevel ?? MAX_DEPTH;
  for (let z = zFrom; z <= zTo; z += 1) {
    const a = worldX / HALF_W;
    const b = (worldY - z * ELEV_STEP) / HALF_H;
    const tx = Math.floor((a + b) / 2);
    const ty = Math.floor((b - a) / 2);
    if (!inBounds(tx, ty)) continue;
    const depth = terrain.depth[colOf(tx, ty)] ?? 0;
    if (depth === z) return { x: tx, y: ty, z };
    fallback = { x: tx, y: ty, z: depth };
  }
  return fallback;
}
